import React, { useState, useEffect, useCallback } from 'react';
import { IconSearch, IconRefresh } from '@tabler/icons-react';
import api from '../api/api';

const ACTION_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'LOGIN', label: '로그인' },
  { value: 'LOGIN_FAIL', label: '로그인 실패' },
  { value: 'CREATE', label: '생성' },
  { value: 'UPDATE', label: '수정' },
  { value: 'DELETE', label: '삭제' },
  { value: 'IMPERSONATE', label: '강제로그인' },
];

function formatDate(str) {
  if (!str) return '-';
  const d = new Date(str);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function actionBadge(action) {
  const map = {
    LOGIN: 'bg-success',
    LOGIN_FAIL: 'bg-danger',
    CREATE: 'bg-primary',
    UPDATE: 'bg-warning',
    DELETE: 'bg-danger',
    IMPERSONATE: 'bg-purple',
  };
  return map[action] || 'bg-secondary';
}

export default function AuditLogPage({ me, showToast }) {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  // 필터
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const toDateStr = (d) => d.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(toDateStr(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(toDateStr(today));
  const [action, setAction] = useState('');

  const fetchLogs = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, size: 20 });
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (action) params.append('action', action);

      const res = await api.get(`/admin/audit-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalElements(res.data.totalElements || 0);
      setPage(p);
    } catch (err) {
      showToast('활동 로그를 불러올 수 없습니다.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, action, showToast]);

  useEffect(() => { fetchLogs(0); }, [fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs(0);
  };

  return (
    <div className="container-xl">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">활동 로그</h2>
            <div className="text-muted mt-1">총 {totalElements}건</div>
          </div>
        </div>
      </div>

      {/* 필터 */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <form onSubmit={handleSearch} className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-0" style={{ fontSize: 12 }}>시작일</label>
              <input type="date" className="form-control form-control-sm"
                value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label mb-0" style={{ fontSize: 12 }}>종료일</label>
              <input type="date" className="form-control form-control-sm"
                value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label mb-0" style={{ fontSize: 12 }}>액션</label>
              <select className="form-select form-select-sm"
                value={action} onChange={e => setAction(e.target.value)}>
                {ACTION_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto d-flex gap-1">
              <button type="submit" className="btn btn-primary btn-sm">
                <IconSearch size={14} className="me-1" />검색
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm"
                onClick={() => { setStartDate(toDateStr(thirtyDaysAgo)); setEndDate(toDateStr(today)); setAction(''); }}>
                <IconRefresh size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 테이블 */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter card-table table-striped">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>시간</th>
                <th style={{ whiteSpace: 'nowrap' }}>사용자</th>
                <th style={{ whiteSpace: 'nowrap' }}>액션</th>
                <th style={{ whiteSpace: 'nowrap' }}>대상</th>
                <th>상세</th>
                <th style={{ whiteSpace: 'nowrap' }}>IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-4">로딩 중...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-4 text-muted">로그가 없습니다.</td></tr>
              ) : logs.map(log => (
                <tr key={log.id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{formatDate(log.createdAt)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{log.userName || '-'}</td>
                  <td>
                    <span className={`badge ${actionBadge(log.action)}`} style={{ fontSize: 10 }}>{log.action}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {log.entityType}{log.entityId ? `#${log.entityId}` : ''}
                  </td>
                  <td style={{ fontSize: 13, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.detail || '-'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{log.ipAddress || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="card-footer d-flex justify-content-center">
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => fetchLogs(page - 1)}>이전</button>
              </li>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                const startP = Math.max(0, Math.min(page - 4, totalPages - 10));
                const p = startP + i;
                if (p >= totalPages) return null;
                return (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => fetchLogs(p)}>{p + 1}</button>
                  </li>
                );
              })}
              <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => fetchLogs(page + 1)}>다음</button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
